import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { consumerGroupsApi } from '../api/consumerGroupsApi';
import type { ConsumerGroupFilters, ConsumerGroupFormValues } from '../types/consumerGroup';

export function useConsumerGroupsQuery(
  filters: ConsumerGroupFilters,
  page: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['gateway', 'consumer-groups', filters, page],
    queryFn: () => consumerGroupsApi.list({ ...filters, page, size: 20, sort: 'createdAt,desc' }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 30_000,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useConsumerGroupQuery(id: string | null) {
  return useQuery({
    queryKey: ['gateway', 'consumer-group', id],
    queryFn: () => consumerGroupsApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateConsumerGroup(onSuccess?: (name: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConsumerGroupFormValues) => consumerGroupsApi.create(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['gateway', 'consumer-groups'] });
      onSuccess?.(created.name);
    },
  });
}

export function useReplaceConsumerGroup(id: string, onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ConsumerGroupFormValues) => consumerGroupsApi.replace(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'consumer-groups'] });
      qc.invalidateQueries({ queryKey: ['gateway', 'consumer-group', id] });
      onSuccess?.();
    },
  });
}

export function useDeleteConsumerGroup(onSuccess?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => consumerGroupsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gateway', 'consumer-groups'] });
      onSuccess?.();
    },
  });
}
